import { X, AlertCircle } from 'lucide-react';
import type { Room } from '../types';
import { formatDateDDMMYYYY } from '../utils/format';

interface LatePaymentInfo {
  roomNumber: string;
  residentName: string;
  daysLate: number;
  nextPaymentDate: string;
}

interface LatePaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  rooms: Room[];
  onRoomClick?: (roomNumber: string) => void;
}

export function LatePaymentsModal({ isOpen, onClose, rooms, onRoomClick }: LatePaymentsModalProps) {
  if (!isOpen) return null;

  // Get late payments data
  const getLatePayments = (): LatePaymentInfo[] => {
    const today = new Date();
    const latePayments: LatePaymentInfo[] = [];

    rooms.forEach(room => {
      room.residents.forEach(resident => {
        if (resident.paymentPeriods.length > 0) {
          const lastPeriod = resident.paymentPeriods[resident.paymentPeriods.length - 1];
          const nextPaymentDate = new Date(lastPeriod.nextPaymentDate);
          
          if (nextPaymentDate < today) {
            const daysLate = Math.floor((today.getTime() - nextPaymentDate.getTime()) / (1000 * 60 * 60 * 24));
            
            // Skip if DP not yet paid (already shown in different panel)
            const hasDPOnly = resident.paymentPeriods.some(p => p.category === 'DP') && 
              !resident.paymentPeriods.some(p => p.category === 'Pelunasan');
            
            if (!hasDPOnly) {
              latePayments.push({
                roomNumber: room.number,
                residentName: resident.name,
                daysLate: daysLate,
                nextPaymentDate: lastPeriod.nextPaymentDate,
              });
            }
          }
        }
      });
    });

    return latePayments.sort((a, b) => b.daysLate - a.daysLate);
  };

  const latePayments = getLatePayments();

  const handleRoomClick = (roomNumber: string) => {
    if (onRoomClick) {
      onRoomClick(roomNumber);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b-2 border-light-border dark:border-dark-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-error-600">
              <AlertCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-light-text dark:text-dark-text">
                Daftar Pembayaran Tertunggak
              </h2>
              <p className="text-sm text-light-textSecondary dark:text-dark-textSecondary">
                Total: {latePayments.length} penghuni
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-light-surfaceAlt dark:hover:bg-dark-surfaceAlt transition-colors"
          >
            <X className="h-6 w-6 text-light-textSecondary dark:text-dark-textSecondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto">
          {latePayments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-lg text-light-textSecondary dark:text-dark-textSecondary">
                Tidak ada pembayaran tertunggak
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {latePayments.map((payment, index) => (
                <div
                  key={`${payment.roomNumber}-${index}`}
                  onClick={() => handleRoomClick(payment.roomNumber)}
                  className="flex items-center justify-between p-4 rounded-xl bg-error-50 dark:bg-error-900/30 border-2 border-error-200 dark:border-error-700 hover:bg-error-100 dark:hover:bg-error-800/50 cursor-pointer transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-error-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-base font-bold text-light-text dark:text-dark-text">
                        Kamar {payment.roomNumber}
                      </p>
                      <p className="text-sm text-light-textSecondary dark:text-dark-textSecondary">
                        {payment.residentName}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-error-700 dark:text-error-400">
                      {payment.daysLate} hari
                    </p>
                    <p className="text-xs text-light-textMuted dark:text-dark-textMuted">
                      Jatuh tempo: {formatDateDDMMYYYY(payment.nextPaymentDate)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t-2 border-light-border dark:border-dark-border">
          <button
            onClick={onClose}
            className="btn-secondary w-full"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
